import axios from "axios";
import type { AxiosError } from "axios";
import { reportUnstableConnection } from "@zenith/utils/Helpers/networkHealth";

let installed = false;

const isOnline = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine;

const isConnectivityFailure = (error: unknown): boolean => {
  if (axios.isCancel(error)) return false;
  const err = error as AxiosError & { code?: string };
  if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
    return false;
  }
  return !!err && typeof err === "object" && !err.response;
};

type NetworkInformation = {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  saveData?: boolean;
  addEventListener?: (type: "change", listener: () => void) => void;
};

const getNetworkInformation = (): NetworkInformation | undefined => {
  if (typeof navigator === "undefined") return undefined;
  const nav = navigator as Navigator & {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
};

const watchNetworkInformation = (): void => {
  const connection = getNetworkInformation();
  if (!connection || typeof connection.addEventListener !== "function") return;

  const evaluate = () => {
    if (
      connection.effectiveType === "slow-2g" ||
      connection.effectiveType === "2g" ||
      connection.saveData === true
    ) {
      reportUnstableConnection();
    }
  };

  connection.addEventListener("change", evaluate);
  evaluate();
};

export const installConnectionWatch = (): void => {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (isConnectivityFailure(error) && isOnline()) {
        reportUnstableConnection();
      }
      return Promise.reject(error);
    }
  );

  watchNetworkInformation();
};
